import type { StyleRule } from '@archipro-design/aria';
import { pxToRem, getScrollbarStyle } from '@archipro-design/aria';
import { getDvhStyles } from '~/modules/moodboards/util';

export const PageWrapper: StyleRule = ({ theme }) => ({
    /**
     * Containing block for the backdrop, which is an absolutely positioned
     * `layout="fill"` image plus its scrim. Without a positioned ancestor both
     * escape to the initial containing block, and — being positioned — paint
     * over every unpositioned background beneath them, the auth card's own
     * included.
     */
    position: 'relative',
    /**
     * `minHeight`, not `height`: the card is a bottom sheet on a phone and a
     * long step can outgrow the viewport. A fixed height would let the page
     * scroll past the backdrop and end on bare white.
     */
    ...getDvhStyles('minHeight', '', 100),
    [theme.screen.tablet]: {
        ...getDvhStyles('height', '', 100),
        width: '100%',
        backgroundColor: theme.siteVariables.colors.primitive.black,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
});

export const ContentWrap: StyleRule = ({ theme }) => ({
    /**
     * Lifts the whole column over the backdrop image and its scrim. They sit
     * earlier in the DOM but are positioned, so without this the column's own
     * backgrounds paint underneath them while its positioned descendants paint
     * on top — a card with visible copy and no surface behind it.
     *
     * The tablet rule below re-positions this box; the stacking order is what
     * carries across both.
     */
    position: 'relative',
    zIndex: 1,
    [theme.screen.tablet]: {
        position: 'absolute',
        inset: '0',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        paddingTop: pxToRem(24),
    },
});

export const Content: StyleRule = ({ theme }) => ({
    width: '100%',
    flexGrow: 1,
    [theme.screen.tablet]: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
});

export const LogoWrapper: StyleRule = ({ theme }) => ({
    display: 'none',
    color: 'white',
    [theme.screen.tablet]: {
        paddingLeft: pxToRem(128),
        display: 'flex',
        flexBasis: '55%',
        justifyContent: 'center',
        alignSelf: 'center',
    },
});

export const LoginWrapper: StyleRule = ({ theme }) => ({
    [theme.screen.tablet]: {
        ...getDvhStyles('maxHeight', '', 92),
        overflowY: 'auto',
        ...getScrollbarStyle(theme, '0'),
    },
});

export const Links: StyleRule = ({ theme }) => ({
    display: 'none',
    [theme.screen.tablet]: {
        display: 'flex',
        paddingBottom: pxToRem(24),
        gap: pxToRem(60),
        justifyContent: 'center',
        width: '100%',
    },
    '& a': {
        color: theme.siteVariables.colors.white['000'],
        '&:hover': {
            textDecoration: 'underline',
        },
    },
});
